import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useJourneyAction, useUpdateJourneyAction } from 'api/journey-actions/hooks';
import { ACTION_TYPES } from 'ui/actions/action-config';
import {
  ActionFooter,
  ActionHeader,
  ActionSidebarLoading,
  SidebarProps,
  createActionForm,
  useCreateActionFormStore,
} from 'ui/journey-builder/node-types/common';

import {
  DurationFields,
  DurationRequirement,
  DurationSummary,
  LOOP_MINIMUM_MINUTES,
  getDurationMinutes,
  normalizeDuration,
  toDurationValues,
} from '../duration';

type Data = {
  days: string;
  hours: string;
  minutes: string;
};

const ACTION_TYPE = ACTION_TYPES.journey_loop;

const Form = createActionForm<Data>();

export const SidebarContent = (props: SidebarProps) => {
  const { journeyActionId } = props;
  const { data, isLoading } = useJourneyAction<typeof ACTION_TYPE>(journeyActionId);
  const { t } = useScopedTranslate(sidebarLocale);
  const formStore = useCreateActionFormStore();
  const isDirty = formStore.useIsDirty();

  const days = formStore.useFieldValue<string>('days') ?? '';
  const hours = formStore.useFieldValue<string>('hours') ?? '';
  const minutes = formStore.useFieldValue<string>('minutes') ?? '';

  const values = data?.values;
  const defaults = normalizeDuration({
    days: values?.days?.value,
    hours: values?.hours?.value ?? '12',
    minutes: values?.minutes?.value,
  });
  const editMutation = useUpdateJourneyAction(journeyActionId);
  const durationMinutes = getDurationMinutes({ days, hours, minutes });
  const isValid = durationMinutes >= LOOP_MINIMUM_MINUTES;
  const isDurationDirty = durationMinutes !== getDurationMinutes(defaults);

  const handleSave = (data: Data) => {
    const values = toDurationValues(data, LOOP_MINIMUM_MINUTES);
    if (!values) return false;
    return editMutation.mutateAsync({
      action: ACTION_TYPE,
      isAutomatic: true,
      values,
      description: null,
    });
  };

  if (isLoading || !data) {
    return <ActionSidebarLoading actionType={ACTION_TYPE} {...props} />;
  }

  return (
    <Sidebar>
      <ActionHeader actionType={ACTION_TYPE} {...props} />
      <Sidebar.Content>
        <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
          <Stack space={6}>
            <HideContainer title={t('actionDetails')}>
              <Text font="regular14" text={t('loopDescription')} />
            </HideContainer>
            <Sidebar.Separator />
            <HideContainer title={t('loopTimer')}>
              <Stack space={4}>
                <DurationFields form={Form} defaults={defaults} />
                <DurationSummary variant="loop" parts={{ days, hours, minutes }} />
                {!isValid && <DurationRequirement variant="loop" isDirty={isDurationDirty} />}
              </Stack>
            </HideContainer>
            <ActionFooter
              action={data}
              isDisabled={!isDirty || editMutation.isLoading || !isValid}
              form={Form}
            />
          </Stack>
        </FormWithUnsavedChangesPrompt>
      </Sidebar.Content>
    </Sidebar>
  );
};

const sidebarLocale = {
  en: {
    actionDetails: 'Action details',
    loopTimer: 'Loop timer',
    loopDescription: 'Use when you want to repeat some actions until a condition is met.',
  },
};
